import { ChangeEvent, forwardRef } from 'react'

export interface SearchInputProps {
  /** Текущее значение поискового запроса */
  value: string
  /** Обработчик изменения значения */
  onChange: (value: string) => void
  /** Плейсхолдер для поля ввода */
  placeholder?: string
  /** Показывать ли кнопку очистки */
  showClearButton?: boolean
  /** Дополнительные CSS классы для контейнера */
  className?: string
  /** Дополнительные CSS классы для input */
  inputClassName?: string
  /** Отключить поле ввода */
  disabled?: boolean
  /** Автофокус при монтировании */
  autoFocus?: boolean
  /** ID для тестирования */
  'data-testid'?: string
}

/**
 * Универсальный компонент поиска с иконкой и опциональной кнопкой очистки.
 * Используется на странице Jobs и на детальной странице вакансии.
 */
const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      placeholder = 'Search...',
      showClearButton = false,
      className = '',
      inputClassName = '',
      disabled = false,
      autoFocus = false,
      'data-testid': testId,
    },
    ref
  ) => {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value)
    }

    const handleClear = () => {
      onChange('')
    }

    return (
      <div className={`relative ${className}`}>
        <input
          ref={ref}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          data-testid={testId}
          className={`w-full pl-10 pr-4 py-2 border border-border-strong rounded-md text-body bg-surface-default placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring focus:border-interactive disabled:bg-surface-subtle disabled:cursor-not-allowed ${inputClassName}`}
        />
        {/* Search Icon */}
        <svg
          className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        {/* Clear Button */}
        {showClearButton && value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-interactive-focus-ring rounded-sm"
            aria-label="Clear search"
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    )
  }
)

SearchInput.displayName = 'SearchInput'

export default SearchInput
